import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState, AppDispatch } from '@center-pwa/store/store';
import { useTranslation } from 'localization';

import './user-sphere.scss';
import CurrentSphereSelect from '../../atoms/current-sphere-dropdown/current-sphere-dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { setSphereId } from '@center-pwa/reducers/sphereReducer';
import { useNavigate, useParams } from 'react-router-dom';
import { SphereAvatar } from '../../../design-system-my-lib';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type { ContextOption } from '../../molecules/ContextSelectorMenu/ContextSelectorMenuMolecule';
import { useSphereSearch } from '../../../hooks/useSphereSearch';
import SphereRowMolecule from '../SphereRow/SphereRowMolecule';
import {
  fetchSphereLineage,
  type LineageChildDto,
  type LineageNodeDto,
} from '../../../api/lineageApi';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import NeedsAHomeListMolecule from '../NeedsAHomeList/NeedsAHomeListMolecule';

interface UserSphereProps {
  sphereId: string;
  onSphereChange: (newSphereId: string) => void;
  metaspaceId: string;
  initialSelectedPath?: string;
  contexts?: ContextOption[];
  contextLoading?: boolean;
  /** Reports which sphere this breadcrumb has finished resolving (null while it is
   *  resolving, or when what it holds describes a different sphere). Sphere-keyed
   *  rather than a boolean so a stale "ready" from the previous sphere can never be
   *  mistaken for readiness for the current one. */
  onLoadedSphereChange?: (loadedSphereId: string | null) => void;
}

const UserSphere: React.FC<UserSphereProps> = ({
  onSphereChange,
  sphereId,
  metaspaceId,
  initialSelectedPath,
  contexts,
  contextLoading,
  onLoadedSphereChange,
}) => {
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient ?? null;
  const searchClient = apiClients?.searchClient ?? null;
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { sphereId: urlSphereId } = useParams<{ sphereId: string }>();
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loadingItems, setLoadingItems] = useState<Record<string, boolean>>({});
  const [expandedParents, setExpandedParents] = useState<
    Record<string, boolean>
  >({});
  const [nestedItems, setNestedItems] = useState<Record<string, unknown[]>>({});
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [dropdownOptions, setDropdownOptions] = useState<any[]>([]);
  // Starts true: this molecule always fetches on mount, and reporting "not loading"
  // for the first frame would let the page think the header was ready and mount the
  // metaspace, only to tear it down again when the fetch flips this to true.
  const [loadingDropdown, setLoadingDropdown] = useState<boolean>(true);
  const userId = useSelector((state: RootState) => state.userProfile?.userId);
  const [sphereData, setSphereData] = useState<any>(null);
  const [isLeftTruncated, setIsLeftTruncated] = useState(true);

  // Local context state (selection lives within this component)
  const [selectedContext, setSelectedContext] = useState<string | null>(null);

  // Auto-select the default context when contexts load so the chip
  // matches the context actually used for the initial data fetch.
  const DEFAULT_CONTEXT = 'center_hierarchy_context';
  useEffect(() => {
    if (selectedContext !== null || !contexts || contexts.length === 0) return;
    const defaultCtx = contexts.find(c => c.systemName === DEFAULT_CONTEXT);
    if (defaultCtx) {
      setSelectedContext(defaultCtx.systemName);
    }
  }, [contexts, selectedContext]);

  const level0Filters = useMemo(() => {
    if (!selectedContext || !contexts) return null;
    const ctx = contexts.find(c => c.systemName === selectedContext);
    return ctx?.level0PropertyFilters ?? null;
  }, [selectedContext, contexts]);

  const allLevelFilters = useMemo(() => {
    if (!selectedContext || !contexts) return null;
    const ctx = contexts.find(c => c.systemName === selectedContext);
    return ctx?.allLevelPropertyFilters ?? null;
  }, [selectedContext, contexts]);

  // Reset expansion state when context changes, re-search if active
  const handleContextSelect = useCallback((systemName: string | null) => {
    setSelectedContext(systemName);
    setExpandedParents({});
    setNestedItems({});
  }, []);

  // Search state — query drives the controlled input; results/loading/error come from useSphereSearch
  const [searchQuery, setSearchQuery] = useState('');

  const hookContextFilters = useMemo(
    () => (level0Filters && level0Filters.length > 0 ? level0Filters : undefined),
    [level0Filters],
  );
  const hookContextLevelFilters = useMemo(
    () => (allLevelFilters && allLevelFilters.length > 0 ? allLevelFilters : undefined),
    [allLevelFilters],
  );

  const {
    results: sphereSearchRaw,
    loading: isSearching,
    error: sphereSearchErrorMsg,
    setQuery: setSphereQuery,
  } = useSphereSearch({
    contextFilters: hookContextFilters,
    contextLevelFilters: hookContextLevelFilters,
  });

  const searchError = !!sphereSearchErrorMsg;

  // Map useSphereSearch results to the flat option shape the dropdown expects.
  // Returns empty when no query so the tree shows instead of stale results.
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return sphereSearchRaw.map((result) => ({
      name: result.label || result.properties?.name || '',
      id: result.id,
      level: 0,
      fullPath: result.label || result.properties?.name || '',
      isSubItem: true,
      hasSubItems: false,
      glyph: result.glyph || '',
      glyphColor: result.glyphColor,
      image: result.properties?.imageId || null,
      isSearchResult: true,
      parentPath: result.parentPath,
      isPrivate: result.isPrivate,
    }));
  }, [sphereSearchRaw, searchQuery]);

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setSphereQuery(query);
    },
    [setSphereQuery],
  );

  // The context actually used to fetch. Derived rather than depended on directly:
  // `selectedContext` starts null and is then auto-set to DEFAULT_CONTEXT by the
  // effect above, which used to re-run this fetch with an *identical* context name —
  // issuing the tree + ancestors calls twice per sphere and writing the breadcrumb
  // twice. Depending on the resolved name makes that transition a no-op.
  const contextName = selectedContext || DEFAULT_CONTEXT;

  // Guards against out-of-order responses. Clicking through Atlas rows starts a new
  // fetch before the previous one lands; without this, a slow response for an earlier
  // sphere overwrites a newer one and the breadcrumb jumps back to a stale path.
  const fetchSeqRef = useRef(0);
  // Which sphere the data we're currently holding describes.
  const loadedSphereIdRef = useRef<string | null>(null);
  // Which sphere we have finished resolving — set whether the fetch succeeded or not,
  // so a sphere with no root (or a failed request) still reports in and can't stall the
  // page's header-first gate forever.
  const [resolvedSphereId, setResolvedSphereId] = useState<string | null>(null);

  // The default (center hierarchy) context is the indexed center lineage, served by the
  // search service: ancestors and direct children in two small ES reads. Any other
  // context is a real traversal and stays on the graph context executor.
  const isHierarchyContext = contextName === DEFAULT_CONTEXT;

  useEffect(() => {
    // Guarded on the client alone: its presence already implies a signed-in session, and
    // the bearer token is resolved per-request.
    if (!sphereId) return;
    const client = isHierarchyContext ? searchClient : graphClient;
    if (!client) return;

    const seq = ++fetchSeqRef.current;

    // Only show the loader when what we're holding describes a *different* sphere —
    // otherwise the breadcrumb would render the previous sphere's path as though it
    // were current. A same-sphere refetch (e.g. the user picks another context) keeps
    // the current value on screen so the open dropdown isn't torn down underneath them.
    if (loadedSphereIdRef.current !== sphereId) {
      setLoadingDropdown(true);
    }

    const fetchData = async () => {
      try {
        const next = isHierarchyContext
          ? await fetchHierarchySphereData(client, sphereId, graphClient)
          : await fetchContextSphereData(client, contextName, sphereId);
        if (seq !== fetchSeqRef.current) return; // superseded by a newer sphere

        if (next) {
          loadedSphereIdRef.current = sphereId;
          setSphereData({
            // Which sphere this data describes — the breadcrumb effects below use it to
            // ignore data (and the seed prop) that belongs to a different sphere.
            forSphereId: sphereId,
            ...next,
          });
          // Mark current sphere as expanded so the chevron shows ▲
          setExpandedParents(prev => ({ ...prev, [next.id]: true }));
        }
      } catch (error) {
        console.error('Failed to fetch breadcrumb data:', error);
      } finally {
        if (seq === fetchSeqRef.current) {
          setLoadingDropdown(false);
          setResolvedSphereId(sphereId);
        }
      }
    };

    fetchData();
  }, [
    sphereId,
    graphClient,
    searchClient,
    contextName,
    isHierarchyContext,
  ]);

  // Surface *which sphere* this breadcrumb has resolved. On the first render after
  // `sphereId` changes, `resolvedSphereId` still holds the previous sphere, so this is
  // null immediately — no window in which the page could read a stale "ready" and mount
  // the metaspace against the new sphere before the header has caught up.
  const loadedSphereId = resolvedSphereId === sphereId ? sphereId : null;

  // "Needs a home" is a Center-wide worklist, so it belongs to Centers only — mounting it on
  // every sphere fired an unplaced probe on each sphere selection. Read from the data that
  // already describes *this* sphere, so no stale type survives a sphere change.
  const isCenter =
    !!sphereId && sphereData?.forSphereId === sphereId && sphereData?.properties?.type === 'center_organization';

  useEffect(() => {
    onLoadedSphereChange?.(loadedSphereId);
  }, [loadedSphereId, onLoadedSphereChange]);

  const generatePathFromAncestors = (ancestors: any[]): string => {
    const pathArray: string[] = [];

    const traverseAncestors = (node: any) => {
      if (node.ancestors && node.ancestors.length > 0) {
        traverseAncestors(node.ancestors[0]);
      }
      if (node.properties?.name) {
        pathArray.push(node.properties.name);
      }
    };

    if (ancestors.length > 0) {
      traverseAncestors(ancestors[0]);
    }

    pathArray.reverse();
    return pathArray.join(' / ');
  };

  const extractBasePath = (): string => {
    const baseSphere = sphereData;
    const ancestors = baseSphere?.ancestors || [];
    const baseName = baseSphere?.properties?.name || 'Center Technologies';

    if (ancestors.length === 0) {
      return baseName;
    } else {
      return `${generatePathFromAncestors(ancestors)} / ${baseName}`;
    }
  };

  // Build hierarchy options from sphere data
  const ancestorsOptions = useMemo(() => {
    if (!sphereData) return [];
    const baseName = sphereData?.properties?.name || 'Center Technologies';
    return generateAncestorOptions(
      sphereData?.ancestors || [],
      baseName,
      sphereData
    );
  }, [sphereData]);

  const spheresOptions = useMemo(() => {
    if (!sphereData) return [];
    const lastAncestor = ancestorsOptions.at(-1);
    return generateSphereOptions(
      sphereData?.nearbySpheres || [],
      lastAncestor?.level ?? -1,
      lastAncestor?.fullPath || '',
      expandedParents,
      nestedItems
    );
  }, [sphereData, ancestorsOptions, expandedParents, nestedItems]);

  // Current sphere defaults to expanded (undefined !== false → true).
  // Collapsing sets it to false; re-expanding sets it back to true.
  // When an ancestor is expanded, insert its children after it in the list.
  const hierarchyOptions = useMemo(() => {
    const currentExpanded = expandedParents[sphereData?.id] !== false;
    const result: any[] = [];

    for (const opt of ancestorsOptions) {
      result.push(opt);
      // If this ancestor is expanded and has loaded children, insert them
      if (opt.isAncestor && expandedParents[opt.id] && nestedItems[opt.id]) {
        const childOptions = generateSphereOptions(
          nestedItems[opt.id] as any[],
          opt.level,
          opt.fullPath,
          expandedParents,
          nestedItems
        );
        result.push(...childOptions);
      }
    }

    if (currentExpanded) {
      result.push(...spheresOptions);
    }

    return result;
  }, [ancestorsOptions, spheresOptions, expandedParents, nestedItems, sphereData?.id]);

  // Determine final dropdown options based on mode
  const finalOptions = useMemo(() => {
    if (searchQuery.trim() !== '') return searchResults;
    return hierarchyOptions;
  }, [searchQuery, searchResults, hierarchyOptions]);

  // The fetched data is the single source of truth for the breadcrumb. Skip while it
  // still describes the previous sphere, otherwise the path would be rebuilt from the
  // old sphere's ancestors before the new ones arrive.
  useEffect(() => {
    if (sphereData && sphereData.forSphereId !== sphereId) return;

    const initialBasePath = extractBasePath();
    setSelectedPath(initialBasePath);

    setDropdownOptions(hierarchyOptions);

    const match = hierarchyOptions.find((option: any) => option.id === urlSphereId);
    if (match) {
      setSelectedPath(match.fullPath);
    }
  }, [sphereData, sphereId, urlSphereId, hierarchyOptions]);

  // `initialSelectedPath` is only a seed for the window before this component has
  // fetched its own data. It arrives from the page's separate sphere fetch, which
  // resolves *after* ours — so letting it write unconditionally overwrote the correct
  // breadcrumb with a second, differently-derived one a moment later.
  useEffect(() => {
    if (initialSelectedPath && sphereData?.forSphereId !== sphereId) {
      setSelectedPath(initialSelectedPath);
    }
  }, [initialSelectedPath, sphereData, sphereId]);

  const toggleParentExpansion = (
    event: React.MouseEvent,
    itemId: string,
    isAncestor: boolean,
    contextLevel?: number
  ) => {
    event.stopPropagation();

    const isExpanded = expandedParents[itemId];

    // Skip fetch for the current sphere — its children come from spheresOptions
    if (!isExpanded && !nestedItems[itemId] && itemId !== sphereData?.id) {
      loadSubItems(itemId, isAncestor, contextLevel);
    }

    setExpandedParents((prevState) => ({
      ...prevState,
      [itemId]: !prevState[itemId],
    }));
  };

  const loadSubItems = async (itemId: string, isAncestor: boolean, contextLevel?: number) => {
    const client = isHierarchyContext ? searchClient : graphClient;
    if (!client) return;
    setLoadingItems((prev) => ({ ...prev, [itemId]: true }));

    try {
      let rawChildren: any[];
      if (isHierarchyContext) {
        // Same lineage read as the initial load, anchored on the expanded sphere.
        const lineage = await fetchSphereLineage(client, itemId, graphClient);
        rawChildren = lineage.children.map(lineageChildToNearbySphere);
      } else if (isAncestor) {
        // Ancestors are above the context tree — execute the full tree from
        // the ancestor as root so children start at context level 0.
        const data = await client.get(`/contexts/${contextName}/execute`, {
          params: { rootSphereId: itemId, responseMode: 'tree', includeEdgeCount: true },
        });
        const roots = data?.roots ?? [];
        rawChildren = roots.length > 0 ? (roots[0].children ?? []) : [];
      } else {
        // Regular children — use the lazy-load endpoint with the parent's
        // context level so the backend resolves the correct child level.
        const data = await client.get(`/contexts/${contextName}/execute/children`, {
          params: {
            parentSphereId: itemId,
            parentLevel: contextLevel ?? 0,
            rootSphereId: sphereId,
            includeEdgeCount: true,
          },
        });
        rawChildren = data?.children ?? [];
      }

      // Store raw SphereNode children — the reactive memo chain
      // (generateSphereOptions) will process them into rendered options
      setNestedItems((prev) => ({
        ...prev,
        [itemId]: rawChildren,
      }));
    } catch (error) {
      console.error('Error fetching sub-items:', error);
    } finally {
      setLoadingItems((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const goToSphere = (targetSphereId: string) => {
    dispatch(setSphereId({ sphereId: targetSphereId, source: 'UserSphere' }));

    const currentPath = globalThis.location.pathname;
    const pathParts = currentPath.split('/');
    const sphereIndex = pathParts.indexOf('sphere');

    if (sphereIndex !== -1) {
      pathParts[sphereIndex + 1] = targetSphereId;
      const newPath = pathParts.join('/');
      navigate(newPath, { replace: true });
    } else {
      const newPath = `/sphere/${targetSphereId}/metaspace/${metaspaceId}`;
      navigate(newPath, { replace: true });
    }

    onSphereChange(targetSphereId);
  };

  const onsphereChange = (e: { value: any }) => {
    const selectedFullPath = `${e.value.fullPath}`;
    setSelectedPath(selectedFullPath);
    goToSphere(e.value.id);

    // Clear search after selection
    if (searchQuery) {
      setSearchQuery('');
      setSphereQuery('');
    }
  };

  const SphereOptionTemplate = (option: any) => {
    // Search results use the canonical compact row instead of the tree template
    if (option.isSearchResult) {
      return (
        <SphereRowMolecule
          variant="compact"
          sphereId={option.id}
          sphereName={option.name}
          glyph={option.glyph || undefined}
          glyphColor={option.glyphColor}
          isPrivate={option.isPrivate}
          parentPath={option.parentPath}
        />
      );
    }

    const isSubItem = option.isSubItem ? 'sub-item' : '';
    const levelClass = `level-${option.level}`;

    const renderLevelDots = (level: number) => {
      return Array.from({ length: level }, (_, index) => (
        <div className="main-Ellipsis" key={index}>
          <span className="level-dot">...</span>
        </div>
      ));
    };

    const isExpanded = expandedParents[option.id];
    const showToggleIcon =
      !option.isSearchResult &&
      (option.hasSubItems || option.edgeCount > 0 || option.isAncestor);
    const toggleIconClass = isExpanded
      ? 'pi pi-chevron-up'
      : 'pi pi-chevron-down';

    const handleBreadcrumbClick = (part: string) => {
      const matchingOption = dropdownOptions.find((option) =>
        option.fullPath.includes(part)
      );

      if (matchingOption) {
        setIsDropdownOpen(true);

        setTimeout(() => {
          const matchingElement = document.getElementById(matchingOption.id);
          if (matchingElement) {
            matchingElement.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            });
          }
        }, 100);
      }
    };

    return (
      <div
        id={option.id}
        className={`sphere-option ${isSubItem} ${levelClass}`}
      >
        {showToggleIcon ? (
          <span
            className={`pr-1 ${toggleIconClass} toggle-icon-padding`}
            onClick={(event) =>
              toggleParentExpansion(event, option.id, option.isAncestor, option.contextLevel)
            }
          ></span>
        ) : (
          <span className="toggle-icon-placeholder"></span>
        )}
        <span
          className={`p-dropdown-item-label ${
            selectedPath === option.fullPath ? 'bold' : ''
          }`}
          role="button"
          tabIndex={0}
          onClick={() => handleBreadcrumbClick(option.name)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleBreadcrumbClick(option.name);
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <div className="avatar-name-container">
            <SphereAvatar
              glyph={option.glyph}
              glyphColor={option.glyphColor}
              imageId={option.image}
              sphereId={option.id}
            />
            {option.level > 0 && renderLevelDots(option.level)}

            <span className="sphere-name">{option.name}</span>
            {loadingItems[option.id] && (
              <ProgressSpinner
                className="custom-spinner"
                style={{
                  width: '20px',
                  height: '20px',
                  marginLeft: '10px',
                  marginTop: '5px',
                }}
                strokeWidth="4"
              />
            )}
          </div>
        </span>
      </div>
    );
  };

  const charLimit = 70;

  const selectedidSphereTemplate = () => {
    const parts = selectedPath.split(' / ');
    const lastPart = parts.pop();

    // Mobile view: only show bold current sphere name
    if (globalThis.innerWidth <= 500) {
      return (
        <div className="sphere-item sphere-item-value">
          <button
            type="button"
            className="bold breadcrumb-link"
          >
            <SphereAvatar
              glyph={sphereData?.glyph || ''}
              glyphColor={sphereData?.glyphColor}
              imageId={sphereData?.properties?.imageId || null}
              sphereId={sphereData?.id || ''}
              size="xsmall"
            />
            {lastPart ?? ''}
          </button>
        </div>
      );
    }

    // Desktop: show full breadcrumb path
    const remainingParts = parts.join(' / ');
    const totalChars = remainingParts.length + (lastPart?.length ?? 0);
    const handleBreadcrumbClick = (part: string) => {
      const fullPath =
        parts.slice(0, parts.indexOf(part) + 1).join(' / ') + ' / ' + part;
      const matchingOption = dropdownOptions.find(
        (option) =>
          option.fullPath === fullPath || option.fullPath.includes(part)
      );
      if (matchingOption) {
        setIsDropdownOpen(true);
        setTimeout(() => {
          const matchingElement = document.getElementById(matchingOption.id);
          if (matchingElement) {
            matchingElement.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            });
          }
        }, 100);
      }
    };
    const toggleTruncation = () => {
      setIsLeftTruncated(!isLeftTruncated);
    };
    if (totalChars <= charLimit) {
      return (
        <div className="sphere-item sphere-item-value">
          {parts.map((part, index) => {
            const matchingOption = dropdownOptions.find((option) =>
              option.fullPath.includes(part)
            );
            return (
              <React.Fragment key={part}>
                <button
                  type="button"
                  className="breadcrumb-link"
                  onClick={() => handleBreadcrumbClick(part)}
                >
                  <SphereAvatar
                    glyph={matchingOption?.glyph || ''}
                    glyphColor={matchingOption?.glyphColor}
                    imageId={matchingOption?.image || null}
                    sphereId={matchingOption?.id || ''}
                    size="xsmall"
                  />
                  {part}
                </button>
                {' / '}
              </React.Fragment>
            );
          })}
          <button
            type="button"
            className="bold breadcrumb-link"
            onClick={() => handleBreadcrumbClick(lastPart ?? '')}
          >
            <SphereAvatar
              glyph={sphereData?.glyph || ''}
              glyphColor={sphereData?.glyphColor}
              imageId={sphereData?.properties?.imageId || null}
              sphereId={sphereData?.id || ''}
              size="xsmall"
            />
            {lastPart ?? ''}
          </button>
        </div>
      );
    }

    const truncatedFromLeft = `...${remainingParts.slice(
      Math.max(0, totalChars - charLimit)
    )}`;
    const truncatedPartsLeft = truncatedFromLeft.split(' / ');

    const truncatedFromRight = `${remainingParts.slice(0, charLimit)}...`;
    const truncatedPartsRight = truncatedFromRight.split(' / ');

    return (
      <div className="sphere-item sphere-item-value">
        {totalChars > charLimit && isLeftTruncated && (
          <span
            className="breadcrumb-link"
            role="button"
            tabIndex={0}
            onClick={toggleTruncation}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                toggleTruncation();
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            .../
          </span>
        )}

        {(isLeftTruncated ? truncatedPartsLeft : truncatedPartsRight).map(
          (part, index) => (
            <React.Fragment key={part}>
              <span
                className="breadcrumb-link"
                role="button"
                tabIndex={0}
                onClick={() => handleBreadcrumbClick(part)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleBreadcrumbClick(part);
                  }
                }}
                style={{ cursor: 'pointer' }}
              >
                {part}
              </span>
              {' / '}
            </React.Fragment>
          )
        )}

        {lastPart && lastPart.length > charLimit ? (
          <>
            <span
              className="bold breadcrumb-link"
              role="button"
              tabIndex={0}
              onClick={() => handleBreadcrumbClick(lastPart)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleBreadcrumbClick(lastPart);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              {lastPart.slice(0, charLimit)}...
            </span>
            <span
              className="breadcrumb-link"
              role="button"
              tabIndex={0}
              onClick={toggleTruncation}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  toggleTruncation();
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              .../
            </span>
          </>
        ) : (
          <span
            className="bold breadcrumb-link"
            role="button"
            tabIndex={0}
            onClick={() => handleBreadcrumbClick(lastPart ?? '')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleBreadcrumbClick(lastPart ?? '');
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {lastPart ?? ''}
          </span>
        )}

        {totalChars > charLimit && !isLeftTruncated && (
          <span
            className="breadcrumb-link"
            onClick={toggleTruncation}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                toggleTruncation();
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            .../
          </span>
        )}
      </div>
    );
  };

  if (!apiClients) {
    return null;
  }

  return (
    <div className="sphere-dropdown">
      {loadingDropdown ? (
        <div className="spinner-container">
          <ProgressSpinner
            className="custom-spinner"
            style={{
              width: '20px',
              height: '20px',
              marginLeft: '10px',
              marginTop: '10px',
            }}
            strokeWidth="4"
          />
        </div>
      ) : (
        <CurrentSphereSelect
          value={selectedPath}
          options={finalOptions}
          onChange={onsphereChange}
          placeholder={t('sphereDropdown.searchPlaceholder')}
          ariaLabel={t('sphereDropdown.label')}
          optionLabel="name"
          filter={true}
          dropdownOpen={isDropdownOpen}
          valueTemplate={selectedidSphereTemplate}
          itemTemplate={(option) => SphereOptionTemplate(option)}
          contexts={contexts}
          selectedContext={selectedContext}
          onContextSelect={handleContextSelect}
          contextLoading={contextLoading}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          isSearching={isSearching}
          searchError={searchError}
        />
      )}
      {isCenter && (
        <div className="sphere-dropdown__purpose">
          <NeedsAHomeListMolecule graphClient={graphClient} centerId={sphereId} />
        </div>
      )}
    </div>
  );
};

/** A lineage node in the sphere shape the option builders below read. */
function lineageNodeToSphere(node: LineageNodeDto) {
  return {
    id: node.id,
    label: node.name ?? '',
    properties: { name: node.name ?? '', imageId: node.imageId ?? undefined },
    glyph: node.glyph ?? '',
    glyphColor: node.glyphColor ?? undefined,
  };
}

/** A direct child as a nearby sphere; `childCount` is the viewable-children count the chevron keys on. */
function lineageChildToNearbySphere(child: LineageChildDto) {
  return {
    ...lineageNodeToSphere(child),
    edgeCount: child.childCount,
    contextLevel: 0,
    nearbySpheres: [],
  };
}

/**
 * Nest a root-first ancestor list the way the graph ancestors route returns it:
 * the root is outermost and each `ancestors[0]` is one level nearer the sphere.
 * `generateAncestorOptions` and `generatePathFromAncestors` walk that shape.
 */
function nestLineageAncestors(ancestors: LineageNodeDto[]): any[] {
  let nested: any[] = [];
  for (let i = ancestors.length - 1; i >= 0; i--) {
    nested = [{ ...lineageNodeToSphere(ancestors[i]), ancestors: nested }];
  }
  return nested;
}

/** Center-hierarchy breadcrumb: one lineage read from the search service, falling back to the graph. */
async function fetchHierarchySphereData(searchClient: ApiClient, sphereId: string, graphClient: ApiClient | null) {
  const lineage = await fetchSphereLineage(searchClient, sphereId, graphClient);
  const sphere = lineageNodeToSphere(lineage.sphere);
  return {
    ...sphere,
    // The context path carries `properties.type` from the traversal; carry the same datum
    // here from the indexed lineage so the header can tell a Center from any other sphere.
    properties: { ...sphere.properties, type: lineage.sphere.schemaSystemName },
    nearbySpheres: lineage.children.map(lineageChildToNearbySphere),
    ancestors: nestLineageAncestors(lineage.ancestors),
  };
}

/**
 * Any other context: a real traversal on the graph context executor (served from
 * its Redis tree cache; the breadcrumb is not a read-after-write surface) plus the
 * center ancestors, which frame the sphere regardless of the context chosen.
 */
async function fetchContextSphereData(graphClient: ApiClient, contextName: string, sphereId: string) {
  const data = await graphClient.get(`/contexts/${contextName}/execute`, {
    params: { rootSphereId: sphereId, responseMode: 'tree', includeEdgeCount: true },
  });
  const root = data?.roots?.[0];
  if (!root) return null;

  let ancestors: any[] = [];
  try {
    const ancestorData = await graphClient.get(`/spheres/${sphereId}/ancestors`);
    ancestors = ancestorData?.ancestors ?? [];
  } catch (ancestorError) {
    console.error('Failed to fetch ancestors:', ancestorError);
  }

  return {
    id: root.id,
    properties: root.properties,
    glyph: root.glyph || '',
    glyphColor: root.glyphColor,
    nearbySpheres: (root.children ?? []).map((child: any) => ({
      id: child.id,
      properties: child.properties,
      label: child.label,
      glyph: child.glyph || '',
      glyphColor: child.glyphColor,
      edgeCount: child.edgeCount ?? 0,
      contextLevel: child.level ?? 0,
      nearbySpheres: child.children ?? [],
    })),
    ancestors,
  };
}

// Helper: generate ancestor options from sphere data.
// Flattens the nested ancestor chain into root-first order so the
// organisation root renders at level 0 and the immediate parent at
// the deepest ancestor level.
function generateAncestorOptions(
  ancestors: any[],
  baseName: string,
  sphereData: any
): any[] {
  // Walk the nested ancestor list to produce a root-first flat array
  const chain: any[] = [];
  const walk = (list: any[]) => {
    if (!list || list.length === 0) return;
    const node = list[0];
    if (node.ancestors?.length) {
      walk(node.ancestors);
    }
    chain.push(node);
  };
  walk(ancestors);
  chain.reverse();

  // Build options from root → immediate parent
  const options: any[] = [];
  let parentPath = '';

  chain.forEach((ancestor: any, index: number) => {
    const name = ancestor.properties?.name || '';
    const fullPath = parentPath ? `${parentPath} / ${name}` : name;
    options.push({
      name,
      id: ancestor.id,
      level: index,
      fullPath,
      isAncestor: true,
      hasSubItems: true,
      glyph: ancestor.glyph || '',
      glyphColor: ancestor.glyphColor,
      image: ancestor.properties?.imageId || null,
    });
    parentPath = fullPath;
  });

  // Add the current sphere at the end
  const currentLevel = chain.length;
  const currentFullPath = parentPath ? `${parentPath} / ${baseName}` : baseName;
  const nearbyCount = sphereData?.nearbySpheres?.length ?? 0;

  options.push({
    name: baseName,
    id: sphereData?.id ?? 'baseName',
    level: currentLevel,
    fullPath: currentFullPath,
    isSubItem: true,
    hasSubItems: nearbyCount > 0,
    edgeCount: nearbyCount,
    parentPath,
    glyph: sphereData?.glyph ?? '',
    glyphColor: sphereData?.glyphColor,
    image: sphereData?.properties?.imageId ?? null,
  });

  return options;
}

// Helper: generate sphere options for nearby spheres
function generateSphereOptions(
  spheres: any[],
  level = 0,
  parentPath = '',
  expandedParents: Record<string, boolean> = {},
  nestedItems: Record<string, unknown[]> = {}
): any[] {
  return spheres
    .map((sphere: any) => {
      const name = sphere.properties?.name ?? '';
      const id = sphere.id;
      const edgeCount = sphere.edgeCount ?? 0;

      const fullPath = `${parentPath}${parentPath ? ' / ' : ''}${name}`;

      const subItems =
        expandedParents[id] && (nestedItems[id] as any[])?.length
          ? generateSphereOptions(nestedItems[id] as any[], level + 1, fullPath, expandedParents, nestedItems)
          : [];

      return [
        {
          name,
          id,
          level: level + 1,
          edgeCount,
          contextLevel: sphere.contextLevel ?? sphere.level ?? 0,
          isSubItem: true,
          fullPath,
          parentPath,
          parentId: sphere.parentId || null,
          glyph: sphere.glyph || '',
          glyphColor: sphere.glyphColor,
          image: sphere.properties?.imageId || null,
        },
        ...subItems,
      ];
    })
    .flat();
}

export default UserSphere;
